import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Button, Container, Typography } from "src/UILibrary"
import * as yup from "yup"
import { useRecoilState } from "recoil"
import InputNameField from "src/components/atom/InputNameField"
import SelectField from "src/components/atom/SelectField"
import { ShowToast } from "src/states/showToast"
import { validateFields } from "src/modules/validation"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import { useMediaQuery } from "react-responsive"
import { ProfileService } from "src/openapi"
import { ShowProgress } from "src/states/showProgress"

export const AdminProfilePage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "signup" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { areas } = useLoginContext()

  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameJP, setFirstNameJP] = useState<string>("")
  const [lastNameJP, setLastNameJP] = useState<string>("")
  const [areaID, setAreaID] = useState<number>(1)
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")

  const { accessToken, checkLoginStatus, initialized, awsID } = useLoginContext()

  useEffect(() => {
    if (!initialized) {
      return
    }
    handleGetProfile()
  }, [awsID, initialized])

  const handleGetProfile = () => {
    ProfileService.adminDetail(accessToken, awsID)
      .then((res) => {
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setFirstName(res.data?.firstName!)
          setFirstNameJP(res.data?.firstNameKana!)
          setLastName(res.data?.lastName!)
          setLastNameJP(res.data?.lastNameKana!)
          setAreaID(res.data?.areaId!)
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const validate = async () => {
    let validation = true

    try {
      await validateFields(
        yup.object().shape({
          firstName: yup.string().required(t("please_input_first_name")),
          lastName: yup.string().required(t("please_input_last_name")),
          firstNameJP: yup.string().required(t("please_input_first_name_jp")),
          lastNameJP: yup.string().required(t("please_input_last_name_jp")),
        }),
        ["firstName", "lastName", "firstNameJP", "lastNameJP"],
        [firstName, lastName, firstNameJP, lastNameJP]
      )
      validation = validation && true
      setNameValidationMessage("")
    } catch (err: any) {
      setNameValidationMessage(err.message)
      validation = validation && false
    }

    return validation
  }

  const handleSave = async () => {
    if (!(await validate())) {
      return false
    }

    setShowProgress(true)
    ProfileService.updateAdmin(accessToken, awsID, {
      firstName: firstName,
      lastName: lastName,
      firstNameKana: firstNameJP,
      lastNameKana: lastNameJP,
      areaId: areaID,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <>
      <Container maxWidth="xl" sx={{ m: "0px", ...(isTablet ? {} : { p: "0px!important" }) }}>
        {isTablet ? (
          <Typography.Large
            color="textPrimary"
            sx={{
              my: "0px",
            }}
          >
            {t("update_admin_profile")}
          </Typography.Large>
        ) : (
          <Typography.SubHeadNormal
            color="textPrimary"
            sx={{
              mt: "20px",
            }}
          >
            {t("update_admin_profile")}
          </Typography.SubHeadNormal>
        )}

        <Box maxWidth="xl" sx={{ px: "0px", mt: isTablet ? "40px" : "25px" }}>
          <InputNameField
            name={t("name")}
            required
            firstName={firstName}
            lastName={lastName}
            firstNameJP={firstNameJP}
            lastNameJP={lastNameJP}
            handleFirstName={setFirstName}
            handleLastName={setLastName}
            handleFirstNameJP={setFirstNameJP}
            handleLastNameJP={setLastNameJP}
            validationMessage={nameValidationMessage}
          ></InputNameField>
          <SelectField
            name={t("address")}
            required
            sx={{ mt: "40px", mb: "40px", width: "200px" }}
            selected={areaID}
            handleSelected={setAreaID}
            datas={areas}
          ></SelectField>
          <Button
            variant="contained"
            sx={{
              minWidth: "240px",
              py: "16px",
              px: "88px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              fontWeight: 700,
              my: "80px",
              mx: "auto",
              display: "flex",
              justifyContent: "center",
              borderRadius: 0,
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => handleSave()}
          >
            {t("change")}
          </Button>
        </Box>
      </Container>
    </>
  )
}
